import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useIndents, useSubmitIndent } from "@/features/indents/api";
import { INDENT_KINDS, INDENT_STATUSES } from "@/features/indents/types";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { IndentPriorityBadge, IndentStatusBadge } from "@/features/indents/badges";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useModuleAccess } from "@/hooks/useIsReadOnly";
import { useBranch } from "@/context/BranchContext";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/indents/")({
  component: IndentsIndex,
});

function IndentsIndex() {
  const navigate = useNavigate();
  const { canWrite } = useModuleAccess("indents");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState<string>("all");
  const [myDepartment, setMyDepartment] = useState(false);
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: allRows = [], isLoading } = useIndents({ search, status: status === "all" ? null : status, branchId: effectiveBranchId, myDepartment });
  const rows = kind === "all" ? allRows : allRows.filter((r) => (r.kind ?? "asset") === kind);
  const submit = useSubmitIndent();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Purchase Indents</h1> <ReadOnlyPill module="indents" />
          <p className="text-sm text-muted-foreground">Multi-stage approvals from HOD to Purchase.</p>
        </div>
        <ActionGuard can={canWrite}>
          <Button onClick={() => navigate({ to: "/indents/new" })}><Plus className="mr-1 h-4 w-4" />New Indent</Button>
        </ActionGuard>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap items-center gap-2">
        <Input className="w-56" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {INDENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {INDENT_KINDS.map((k) => <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center space-x-2 ml-auto">
          <Switch id="my-dept" checked={myDepartment} onCheckedChange={setMyDepartment} />
          <Label htmlFor="my-dept">My Dept Indents</Label>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No indents.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Indent No</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Dept</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead className="text-right">Total Qty</TableHead>
                <TableHead className="text-right">Est. Value</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const its = r.items ?? [];
                  const hasItems = its.length > 0;
                  const totalItems = hasItems ? its.length : 1;
                  const totalQty = hasItems
                    ? its.reduce((s, it) => s + Number(it.quantity_required || 0), 0)
                    : Number(r.qty_required || 0);
                  const totalValue = hasItems
                    ? its.reduce((s, it) => s + Number(it.quantity_required || 0) * Number(it.estimated_unit_cost || 0), 0)
                    : Number(r.qty_required || 0) * Number(r.estimated_unit_cost || 0);
                  return (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/indents/$indentId", params: { indentId: r.id } })}>
                      <TableCell className="font-mono text-xs">
                        <Link to="/indents/$indentId" params={{ indentId: r.id }} className="text-primary hover:underline">{r.indent_no}</Link>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{r.kind ?? "asset"}</Badge></TableCell>
                      <TableCell className="text-xs">{r.creator?.full_name || r.creator?.email || "—"}</TableCell>
                      <TableCell className="text-xs">{r.branch?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-right">{totalItems}</TableCell>
                      <TableCell className="text-right">{totalQty}</TableCell>
                      <TableCell className="text-right">₹ {totalValue.toFixed(2)}</TableCell>
                      <TableCell><IndentPriorityBadge priority={r.priority} /></TableCell>
                      <TableCell><IndentStatusBadge status={r.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.created_at)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {r.status === "Draft" && (
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate({ to: "/indents/$indentId/edit", params: { indentId: r.id } })}>Edit</Button>
                            <Button size="sm" disabled={submit.isPending} onClick={async () => {
                              try {
                                await submit.mutateAsync(r.id);
                                toast.success("Submitted");
                              } catch (e) {
                                toast.error((e as Error).message);
                              }
                            }}>Submit</Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
      </CardContent></Card>
    </div>
  );
}
